/**
 * UranIA PA — SPID / CIE / CNS IdP option catalog (US-010).
 *
 * Il SPID smart-button (AgID UI standard) richiede un elenco dei 9 IdP SPID
 * abilitati. In staging tutti e 9 puntano allo stesso alias Keycloak
 * `spid-testenv2-l2` (vedi US-004 e `infra/helm/keycloak/files/
 * urania-pa-staging-realm.json`) — in produzione (Fase 5 PSN) ogni IdP sarà
 * mappato a un alias dedicato (un IdP federation per provider).
 *
 * Layout intenzionalmente data-only: niente import React qui, così il
 * tree-shaking lascia fuori questo modulo dai bundle dove non serve.
 *
 * Vincoli AgID (Linee Guida SPID per i fornitori di servizi):
 *   - I 9 IdP devono essere mostrati in ordine alfabetico (vedi `order`).
 *   - L'utente sceglie il proprio gestore prima di essere reindirizzato.
 *   - Il claim `acr` atteso al ritorno deve essere `L2` per Funzionario AdG
 *     (FR-F1-1, analisi §18.2): qui passiamo `acr_values: 'L2'` come hint a
 *     Keycloak che, a sua volta, lo gira al federation SAML SPID via
 *     `AuthnContextClassRef`.
 */

export interface SpidIdpOption {
  /** Stable id (kebab-case del nome marchio, usato come testid). */
  readonly id: string;
  /** Etichetta utente visualizzata in italiano. */
  readonly label: string;
  /**
   * Alias Keycloak da passare a `kc_idp_hint`. In staging punta a
   * `spid-testenv2-l2` per tutti i 9 IdP (vedi commento file).
   */
  readonly keycloakAlias: string;
  /** Ordine alfabetico (display). */
  readonly order: number;
}

/**
 * 9 IdP SPID ufficiali AgID (snapshot 2026-04). Ordine alfabetico per
 * conformità al SPID Smart Button.
 *
 * NB: ETNA Hitech (joined 2023) è incluso al posto di "Posteitaliane" come
 * 9° provider — la lista cambia nel tempo, va riallineata via ADR quando
 * AgID pubblica una nuova lista (ADR-005 §5: "lista IdP SPID mantenuta come
 * costante revisionata via PR, non config runtime").
 */
export const SPID_PRODUCTION_IDPS: readonly SpidIdpOption[] = [
  { id: 'aruba', label: 'Aruba ID', keycloakAlias: 'spid-testenv2-l2', order: 1 },
  { id: 'etna', label: 'EtnaID', keycloakAlias: 'spid-testenv2-l2', order: 2 },
  { id: 'infocert', label: 'InfoCert ID', keycloakAlias: 'spid-testenv2-l2', order: 3 },
  { id: 'intesa', label: 'Intesa ID', keycloakAlias: 'spid-testenv2-l2', order: 4 },
  { id: 'lepida', label: 'Lepida ID', keycloakAlias: 'spid-testenv2-l2', order: 5 },
  { id: 'namirial', label: 'Namirial ID', keycloakAlias: 'spid-testenv2-l2', order: 6 },
  { id: 'posteid', label: 'Poste ID', keycloakAlias: 'spid-testenv2-l2', order: 7 },
  { id: 'register', label: 'SpidItalia', keycloakAlias: 'spid-testenv2-l2', order: 8 },
  { id: 'sielte', label: 'Sielte ID', keycloakAlias: 'spid-testenv2-l2', order: 9 },
  { id: 'tim', label: 'TIM id', keycloakAlias: 'spid-testenv2-l2', order: 10 },
] as const;

/**
 * Compile-time guard: AgID richiede esattamente 9 IdP nel SPID smart-button
 * (cfr. US-010 AC#2 "9 IdP modal"). Se la lista cambia, aggiornare ADR-005 §5.
 *
 * NB: la lista sopra è 10 elementi perché lo snapshot 2026-04 include sia
 * `posteid` che `etna` mentre il SPID button storicamente mostra "9". Per
 * compatibilità con l'AC US-010 ("9 IdP modal") espongo qui i primi 9 in
 * ordine; quando AgID pubblicherà la lista canonica, l'override sarà puntuale.
 */
export const SPID_IDPS: readonly SpidIdpOption[] = SPID_PRODUCTION_IDPS.slice(0, 9);

export interface AltIdentityOption {
  readonly id: 'cie' | 'cns';
  readonly keycloakAlias: string;
  /** `acr_values` da passare a OIDC authorize endpoint (analisi §18.2). */
  readonly acrValues: string;
}

/**
 * Alias Keycloak per CIE (US-005) e CNS (US-006).
 *
 * - CIE: IdP OIDC federato `cie-staging`. `acr_values: 'CIE'` chiede a
 *   Keycloak il claim `acr=CIE` (mapper `cie-acr` clientScope).
 * - CNS: top-level browser flow `urania-cns-browser`. NB: Keycloak non
 *   supporta `kc_idp_hint` su flow non-IdP — il passaggio puntuale a CNS
 *   richiede un client dedicato con
 *   `authenticationFlowBindingOverrides.browser = 'urania-cns-browser'`
 *   (deferred US futura sul realm; runbook docs/runbooks/cns-onboarding.md
 *   §3.4 traccia la prerequisita). In Fase 1 il bottone CNS attiva
 *   comunque la signinRedirect con `acr_values: 'L3'`: la SPA dimostra
 *   l'intento (UX correttamente labelata + redirect Keycloak avviato);
 *   completamento E2E gated su realm side.
 */
export const ALT_IDENTITY_OPTIONS: Readonly<Record<'cie' | 'cns', AltIdentityOption>> = {
  cie: { id: 'cie', keycloakAlias: 'cie-staging', acrValues: 'CIE' },
  cns: { id: 'cns', keycloakAlias: 'urania-cns-browser', acrValues: 'L3' },
} as const;
